import React, { useState } from 'react';
import { Sparkles, Heart, Download, Filter, Trash2, Github, Coffee, Menu } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import SupportModal from './SupportModal';

const Header = ({
  categories = [],
  selectedCategoryId,
  onSelectCategory,
  favorites = [],
  onDownload
}) => {
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  return (
    <header className="h-[64px] bg-[#0F172A] border-b border-white/5 flex items-center justify-between px-8 sticky top-0 z-50 backdrop-blur-xl">
      <div className="flex items-center gap-4 md:gap-10 h-full">
        {/* Mobile Menu Trigger */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-slate-400">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#0F172A] border-white/10 w-72">
              <SheetHeader className="mb-8">
                <SheetTitle className="text-white font-black text-xl tracking-tighter flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" /> I <Heart className="w-4 h-4 text-violet-400 fill-current" /> ANIMATION
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      onSelectCategory(cat.id);
                    }}
                    className={cn(
                      "px-4 py-3 text-[11px] font-black uppercase tracking-[0.2em] transition-all rounded-xl text-left",
                      selectedCategoryId === cat.id
                        ? "bg-violet-600/10 text-violet-400 border border-violet-500/20"
                        : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                    )}
                  >
                    {cat.name}
                  </button>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        {/* Logo */}
        <div className="flex items-center gap-3 cursor-pointer group">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center transition-all duration-500 group-hover:rotate-[15deg] group-hover:scale-110 shadow-lg shadow-violet-500/20">
            <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-white animate-pulse" />
          </div>
          <span className="text-white font-black text-lg md:text-xl tracking-tighter flex items-center gap-2">
            <span className="hidden sm:inline">I</span> <Heart className="w-3 h-3 md:w-4 md:h-4 text-violet-400 fill-current" /> <span className="hidden sm:inline text-violet-100">ANIMATION</span>
          </span>
        </div>

        {/* Desktop Main Categories */}
        <nav className="hidden md:flex items-center h-full gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => onSelectCategory(cat.id)}
              className={cn(
                "h-full px-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative flex items-center",
                selectedCategoryId === cat.id
                  ? "text-violet-400"
                  : "text-slate-500 hover:text-slate-300"
              )}
            >
              {cat.name}
              {selectedCategoryId === cat.id && (
                <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-violet-500 shadow-[0_0_12px_rgba(139,92,246,0.8)]" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <a
          href="https://github.com"
          target="_blank"
          rel="noreferrer"
          className="text-slate-500 hover:text-white transition-all transform hover:scale-110 hidden sm:block"
        >
          <Github className="w-5 h-5" />
        </a>
        <button
          onClick={() => setIsSupportOpen(true)}
          className="flex items-center gap-2 group/coffee bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 hover:border-amber-500/40 px-3 md:px-4 py-1.5 md:py-2 rounded-full transition-all duration-300 transform hover:-translate-y-0.5"
        >
          <Coffee className="w-3 h-3 md:w-4 md:h-4 text-amber-500 group-hover/coffee:rotate-[20deg] transition-transform" />
          <span className="text-amber-200 font-black text-[8px] md:text-[9px] uppercase tracking-wider">
            <span className="hidden xs:inline">Buy me a coffee</span>
            <span className="xs:hidden">Support</span>
          </span>
        </button>

        <div className="h-4 w-[1px] bg-white/10 mx-1" />

        <Button
          variant="ghost"
          size="sm"
          onClick={onDownload}
          className="text-slate-400 hover:text-violet-400 p-0 h-auto font-black text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2"
        >
          <span className="w-5 h-5 rounded-full bg-violet-500/10 flex items-center justify-center text-[10px] border border-violet-500/20 font-bold">
            {favorites.length}
          </span>
          Favorites
        </Button>
      </div>

      <SupportModal
        isOpen={isSupportOpen}
        onClose={() => setIsSupportOpen(false)}
      />
    </header>
  );
};

export default Header;
